import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { API_URL } from '../../config';
import BadgeVerifie from '../verification/BadgeVerifie';
import TrustScore from '../verification/TrustScore';
import { formatPrix } from '../../utils/currency';
import { useI18n } from '../../i18n/I18nContext';
import './ProfilePage.css';

// Simule des stats reproductibles à partir de l'id de l'annonce
function simStat(id, base, range) {
  const seed = String(id).split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return base + (seed % range);
}

// Tendance simulée sur 30 jours (-18% à +22%)
function simTrend(id, mult) {
  const seed = String(id).split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return ((seed * mult * 7) % 41) - 18;
}

function ProfilePage() {
  const { t } = useI18n();
  const { accessToken, updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [mesAnnonces, setMesAnnonces] = useState([]);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({ nom: '', prenom: '', telephone: '' });

  useEffect(() => {
    fetchProfile();
    fetchMesAnnonces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API_URL}/users/profile`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      const data = await res.json();
      setProfile(data);
      setFormData({ nom: data.nom, prenom: data.prenom, telephone: data.telephone || '' });
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMesAnnonces = async () => {
    try {
      const res = await fetch(`${API_URL}/annonces/user/mine`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      const data = await res.json();
      // /user/mine retourne un tableau directement
      setMesAnnonces(Array.isArray(data) ? data.slice(0, 5) : (data.annonces || []).slice(0, 5));
    } catch (err) { /* ignore */ }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/users/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (res.ok) {
        updateUser(data.user);
        setEditing(false);
        fetchProfile();
      }
    } catch (err) {
      console.error(err);
    }
  };

  if (!profile) return <div className="loading">{t.profile.loading}</div>;

  return (
    <div className="profile-page">
      <div className="profile-header glass-card">
        <div className="profile-avatar">
          {profile.prenom[0]}{profile.nom[0]}
        </div>
        <div className="profile-info">
          <h2>
            {profile.prenom} {profile.nom}
            {profile.phone_verified && <BadgeVerifie />}
          </h2>
          <span className="profile-role">{profile.role}</span>
          <TrustScore score={profile.trust_score} />
        </div>
      </div>

      <div className="profile-details glass-card">
        <h3>{t.profile.personalInfo}</h3>

        {editing ? (
          <form onSubmit={handleSave}>
            <div className="form-group">
              <label>{t.auth.lastName}</label>
              <input value={formData.nom} onChange={e => setFormData({...formData, nom: e.target.value})} />
            </div>
            <div className="form-group">
              <label>{t.auth.firstName}</label>
              <input value={formData.prenom} onChange={e => setFormData({...formData, prenom: e.target.value})} />
            </div>
            <div className="form-group">
              <label>{t.auth.phone}</label>
              <input value={formData.telephone} onChange={e => setFormData({...formData, telephone: e.target.value})} />
            </div>
            <div className="profile-actions">
              <button type="submit" className="btn btn-primary">{t.profile.saveBtn}</button>
              <button type="button" className="btn btn-secondary" onClick={() => setEditing(false)}>{t.common.cancel}</button>
            </div>
          </form>
        ) : (
          <div className="profile-fields">
            <div className="profile-field">
              <span className="field-label">{t.auth.email}</span>
              <span className="field-value">{profile.email}</span>
            </div>
            <div className="profile-field">
              <span className="field-label">{t.auth.lastName}</span>
              <span className="field-value">{profile.nom}</span>
            </div>
            <div className="profile-field">
              <span className="field-label">{t.auth.firstName}</span>
              <span className="field-value">{profile.prenom}</span>
            </div>
            <div className="profile-field">
              <span className="field-label">{t.auth.phone}</span>
              <span className="field-value">
                {profile.telephone || t.profile.phoneNotSet}
                {profile.phone_verified && ` ${t.profile.phoneVerified}`}
              </span>
            </div>
            <div className="profile-field">
              <span className="field-label">{t.profile.annoncesPub}</span>
              <span className="field-value">{profile.annonces_count}</span>
            </div>
            <div className="profile-field">
              <span className="field-label">{t.profile.memberSince}</span>
              <span className="field-value">{new Date(profile.created_at).toLocaleDateString('fr-FR')}</span>
            </div>

            <div className="profile-actions">
              <button className="btn btn-primary" onClick={() => setEditing(true)}>{t.common.edit}</button>
              {!profile.phone_verified && (
                <Link to="/verify-phone" className="btn btn-secondary">{t.profile.verifyPhone}</Link>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── STATISTIQUES VENDEUR ── */}
      <div className="profile-stats-section glass-card">
        <h3>{t.profile.statsSection}</h3>

        {/* Métriques globales */}
        {(() => {
          const totalVues     = mesAnnonces.reduce((s, a) => s + simStat(a.id, 40, 200), 0);
          const totalContacts = mesAnnonces.reduce((s, a) => s + simStat(a.id, 3, 18), 0);
          const totalFavoris  = mesAnnonces.reduce((s, a) => s + simStat(a.id, 1, 10), 0);
          const seed0 = mesAnnonces.length ? mesAnnonces[0].id : 0;
          const tV = simTrend(seed0, 3);
          const tC = simTrend(seed0, 7);
          const tF = simTrend(seed0, 11);

          const MetricCard = ({ value, label, trend, emoji }) => (
            <div className="stat-metric">
              <span className="stat-metric-emoji">{emoji}</span>
              <span className="stat-metric-value">{Number(value).toLocaleString('fr-DZ')}</span>
              <span className="stat-metric-label">{label}</span>
              {trend !== null && mesAnnonces.length > 0 && (
                <span className={`stat-metric-trend ${trend >= 0 ? 'trend-up' : 'trend-down'}`}>
                  {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
                  <span className="stat-trend-period"> {t.profile.per30d}</span>
                </span>
              )}
            </div>
          );

          return (
            <div className="stats-global">
              <MetricCard value={profile.annonces_count || 0} label={t.profile.annoncesPub} trend={null}  emoji="📋" />
              <MetricCard value={totalVues}     label={t.profile.totalViews}       trend={tV} emoji="👁️" />
              <MetricCard value={totalContacts} label={t.profile.contactsReceived} trend={tC} emoji="📞" />
              <MetricCard value={totalFavoris}  label={t.profile.inFavorites}      trend={tF} emoji="❤️" />
            </div>
          );
        })()}

        {/* Tableau par annonce */}
        {mesAnnonces.length > 0 && (
          <div className="stats-table-wrap">
            <table className="stats-table">
              <thead>
                <tr>
                  <th>{t.profile.annonceCol}</th>
                  <th>{t.profile.priceCol}</th>
                  <th>{t.profile.views}</th>
                  <th>{t.profile.contacts}</th>
                  <th>{t.profile.favorites}</th>
                </tr>
              </thead>
              <tbody>
                {mesAnnonces.map(a => (
                  <tr key={a.id}>
                    <td className="stats-annonce-title">
                      <img src={a.photo} alt={a.titre} />
                      <span>{a.titre}</span>
                    </td>
                    <td className="stats-prix">{formatPrix(a.prix, { showEur: false })}</td>
                    <td>
                      <span className="stats-chip stats-vues">{simStat(a.id, 40, 200)}</span>
                      {(() => { const t = simTrend(a.id, 3); return (
                        <span className={`stats-mini-trend ${t >= 0 ? 'trend-up' : 'trend-down'}`}>
                          {t >= 0 ? '↑' : '↓'}{Math.abs(t)}%
                        </span>
                      ); })()}
                    </td>
                    <td>
                      <span className="stats-chip stats-contacts">{simStat(a.id, 3, 18)}</span>
                    </td>
                    <td>
                      <span className="stats-chip stats-favoris">{simStat(a.id, 1, 10)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {mesAnnonces.length === 0 && (
          <p className="stats-empty">{t.profile.statsEmpty}</p>
        )}
      </div>
    </div>
  );
}

export default ProfilePage;
